import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell
} from '@mui/material';
import { CheckCircleOutline, Close, HighlightOff } from '@mui/icons-material';
import { APIHOST } from '../../common';
import { Order } from '../../types/order';
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import { AvailabilityCalendar, RoomAvailabilityDay, RoomCal, HotelCal } from '@/src/types/availability';
import { HotelChangeRequest, HotelRoomChange } from '../../types/hotelChange';
import { useApp } from '../AppContext';
import { OrderRoom } from '@/src/types/orderRoom';
import { set } from 'date-fns';



interface HotelChangeProps {
  open: boolean;
  onClose: () => void;
  orderDocumentId: string;
}

interface OrderSummary {
  rooms: OrderRoomSummary[];
}
 interface OrderRoomSummary {
  orderRoomId: string;
  currentRoomName: string;
  roomType: string;
  occupantCount: number;
  arrivalDate: string;
  departureDate: string;
  dailyRate: number;
  maxOccupants: number;
  bedCount: number;
 }

 const buildOrderSummary = (order: Order): OrderSummary => {
  const rooms = order.order_rooms?.map(room => ({
    orderRoomId: room.documentId || '',
    currentRoomName: room.conference_hotel_room?.name || 'N/A',
    roomType: room.type || 'N/A',
    occupantCount: room.order_room_occupants ? room.order_room_occupants.length : 0,
    arrivalDate: room.arrivalDate,
    departureDate: room.departureDate,
    dailyRate: room.conference_hotel_room?.dailyRate || 0,
    maxOccupants: room.conference_hotel_room?.maxOccupants || room.order_room_occupants?.length || 1,
    bedCount: room.conference_hotel_room?.bedCount || 1
  })) || [];

  return { rooms };
};

export default function HotelChange({ open, onClose, orderDocumentId }: HotelChangeProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [orderSummary, setOrderSummary] = useState<OrderSummary | null>(null);
  const [availability, setAvailability] = useState<AvailabilityCalendar>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [selectedHotel, setSelectedHotel] = useState<string>('');
  const [roomAssignments, setRoomAssignments] = useState<Record<string, string>>({});
  const [swapHotels, setSwapHotels] = useState(false);

  const {setSnackbarInput} = useApp();

  useEffect(() => {
    if (open && orderDocumentId) {
      // Reset state when dialog opens
      setActiveStep(0);
      setSelectedHotel('');
      setRoomAssignments({});
      setSwapHotels(false);
      setError('');
      
      loadOrderData()
      .then((order) => {
        if (order) {
          setOrderSummary(buildOrderSummary(order));
          loadAvailableRooms(order.conference_group.conference.documentId);
        }
      });
    }
  }, [open, orderDocumentId]);

  const loadOrderData = async (): Promise<Order | null> => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderDocumentId}?populate[]=conference_group.conference&populate[]=conference_hotel.hotel&populate[]=booker&populate[]=order_rooms&populate[]=order_rooms.conference_hotel_room&populate[]=order_rooms.order_room_occupants`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load order data');
      }
      //build a list of all rooms in order. foreeach room gather number of occupants, bed count and daily rate.
      

      const data = await response.json();
      const orderData = data.data as Order;
      setOrder(orderData);
      
      // Set the current hotel as selected by default
      if (orderData.conference_hotel) {
        setSelectedHotel(orderData.conference_hotel.documentId);
      }
      
      return orderData;
    } catch (error) {
      console.error('Error loading order:', error);
      setSnackbarInput({ message: 'Failed to load order data', severity: 'error' });
      setError('Failed to load order data');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRooms = async (conferenceId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(
        `${APIHOST}/api/conferences/${conferenceId}/availability`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        setAvailability(data as AvailabilityCalendar);
      }
    } catch (error) {
      console.error('Error loading available rooms:', error);
      setSnackbarInput({ message: 'Failed to load available rooms', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }


  const getDatesInStay = (arrivalDate: string, departureDate: string): string[] => {
    if (!arrivalDate || !departureDate) return [];

    const dates: string[] = [];
    const current = new Date(`${arrivalDate}T00:00:00`);
    const departure = new Date(`${departureDate}T00:00:00`);

    if (Number.isNaN(current.getTime()) || Number.isNaN(departure.getTime()) || current >= departure) {
      return [];
    }

    while (current < departure) {
      dates.push(current.toISOString().slice(0, 10));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const getHotelAvailabilityScore = (hotel: HotelCal): number => {
    const totals = Object.values(hotel.rooms || {}).reduce(
      (acc, room) => {
        Object.values(room.availability || {}).forEach((day) => {
          acc.allocated += day.allocated || 0;
          acc.available += day.available || 0;
        });
        return acc;
      },
      { allocated: 0, available: 0 }
    );

    if (totals.allocated <= 0) return 0;
    return totals.available / totals.allocated;
  };

  const canHotelMatchAllRooms = (hotel: HotelCal, requestedRooms: OrderRoomSummary[]): boolean => {
    if (!requestedRooms.length) return true;

    const remainingByRoomByDate: Record<string, Record<string, number>> = {};

    Object.values(hotel.rooms || {}).forEach((room) => {
      remainingByRoomByDate[room.documentId] = {};
      Object.entries(room.availability || {}).forEach(([date, day]) => {
        remainingByRoomByDate[room.documentId][date] = day.available || 0;
      });
    });

    const roomsByStrictness = [...requestedRooms].sort((a, b) => {
      if (b.maxOccupants !== a.maxOccupants) return b.maxOccupants - a.maxOccupants;
      if (b.bedCount !== a.bedCount) return b.bedCount - a.bedCount;
      return b.occupantCount - a.occupantCount;
    });

    for (const request of roomsByStrictness) {
      const stayDates = getDatesInStay(request.arrivalDate, request.departureDate);
      if (!stayDates.length) {
        return false;
      }

      const matchingRooms = Object.values(hotel.rooms || {})
        .filter((room) => {
          const roomMaxOccupants = room.maxOccupants || 0;
          const roomBedCount = room.bedCount || 0;
          return roomMaxOccupants >= request.maxOccupants && roomBedCount >= request.bedCount;
        })
        .sort((a, b) => {
          if ((a.maxOccupants || 0) !== (b.maxOccupants || 0)) return (a.maxOccupants || 0) - (b.maxOccupants || 0);
          return (a.bedCount || 0) - (b.bedCount || 0);
        });

      let assigned = false;

      for (const candidateRoom of matchingRooms) {
        const remainingByDate = remainingByRoomByDate[candidateRoom.documentId] || {};
        const hasCapacity = stayDates.every((date) => (remainingByDate[date] || 0) > 0);

        if (!hasCapacity) continue;

        stayDates.forEach((date) => {
          remainingByDate[date] = (remainingByDate[date] || 0) - 1;
        });

        assigned = true;
        break;
      }

      if (!assigned) {
        return false;
      }
    }

    return true;
  };

  const getHotelCriteriaStatus = (hotel: HotelCal, requestedRooms: OrderRoomSummary[]) => {
    if (!requestedRooms.length) {
      return {
        matchesAll: true,
        maxOccupantsMatch: true,
        bedCountMatch: true,
        dateAvailabilityMatch: true,
      };
    }

    const maxOccupantsMatch = requestedRooms.every((request) =>
      Object.values(hotel.rooms || {}).some((room) => (room.maxOccupants || 0) >= request.maxOccupants)
    );

    const bedCountMatch = requestedRooms.every((request) =>
      Object.values(hotel.rooms || {}).some((room) => (room.bedCount || 0) >= request.bedCount)
    );

    const dateAvailabilityMatch = canHotelMatchAllRooms(hotel, requestedRooms);

    return {
      matchesAll: maxOccupantsMatch && bedCountMatch && dateAvailabilityMatch,
      maxOccupantsMatch,
      bedCountMatch,
      dateAvailabilityMatch,
    };
  };

  const requestedRooms = orderSummary?.rooms || [];
  const currentHotelDocumentId = order?.conference_hotel?.documentId;

  const availableRooms = Object.values(availability)
  .filter((hotel) => hotel.documentId !== currentHotelDocumentId)
  .sort((a, b) => {
    const aCanMatchAll = canHotelMatchAllRooms(a, requestedRooms);
    const bCanMatchAll = canHotelMatchAllRooms(b, requestedRooms);

    if (aCanMatchAll !== bCanMatchAll) {
      return aCanMatchAll ? -1 : 1;
    }

    const aAvailabilityScore = getHotelAvailabilityScore(a);
    const bAvailabilityScore = getHotelAvailabilityScore(b);

    if (aAvailabilityScore !== bAvailabilityScore) {
      return bAvailabilityScore - aAvailabilityScore;
    }

    if (a.name && b.name) {
      return a.name.localeCompare(b.name);
    }

    return 0;
  });

  const selectedHotelData = availability[selectedHotel];
  const totalRoomsInOrder = orderSummary?.rooms.length || 0;
  const totalOccupantsInOrder = (orderSummary?.rooms || []).reduce((sum, room) => sum + (room.occupantCount || 0), 0);
  const roomBreakdown = Object.entries(
    (orderSummary?.rooms || []).reduce<Record<string, number>>((acc, room) => {
      const key = room.currentRoomName || 'Unknown Room';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([roomName, count]) => `${count} x ${roomName}`)
    .join(', ');

  const calculateRoomAvailabilityPercent = (availabilityByDate?: Record<string, RoomAvailabilityDay>) => {
    if (!availabilityByDate) return 0;

    const totals = Object.values(availabilityByDate).reduce(
      (acc, day) => {
        acc.allocated += day.allocated || 0;
        acc.available += day.available || 0;
        return acc;
      },
      { allocated: 0, available: 0 }
    );

    if (totals.allocated <= 0) return 0;

    return Math.round((totals.available / totals.allocated) * 100);
  };

  const getMinAvailableRoomsAcrossDates = (availabilityByDate?: Record<string, RoomAvailabilityDay>) => {
    const days = Object.values(availabilityByDate || {});
    if (!days.length) return 'N/A';
    return Math.min(...days.map((day) => day.available || 0));
  };

  const getMaxAvailableRoomsAcrossDates = (availabilityByDate?: Record<string, RoomAvailabilityDay>) => {
    const days = Object.values(availabilityByDate || {});
    if (!days.length) return 'N/A';
    return Math.max(...days.map((day) => day.available || 0));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleHotelSelect = (hotelId: string) => {
    setSelectedHotel(hotelId);
    setRoomAssignments({}); // Reset room assignments when changing hotel
  };

  const handleSwapToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSwapHotels(event.target.checked);
    if (!event.target.checked) {
      // If switching back to "keep hotel", reset to current hotel
      if (order?.conference_hotel) {
        setSelectedHotel(order.conference_hotel.documentId);
      }
    } else {
      // If switching to "swap hotels", clear selection to force user to choose
      setSelectedHotel('');
    }
    setRoomAssignments({});
  };

  const handleProceedToRoomAssignment = () => {
    if (!swapHotels || selectedHotel) {
      setActiveStep(1);
    }
  };

  const handleRoomAssignment = (orderRoomId: string, newRoomId: string) => {
    setRoomAssignments(prev => ({
      ...prev,
      [orderRoomId]: newRoomId
    }));
  };

  const handleChangeHotel = async () => {
    if (!selectedHotel || !order || !order.order_rooms) {
      setError('Missing required data for hotel change');
      return;
    }

    // Convert room assignments to the required format
    const roomChanges: HotelRoomChange[] = order.order_rooms.map(room => ({
      oldRoomId: room.documentId || '',
      newRoomId: roomAssignments[room.documentId || ''] as string
    }));

    // Validate all rooms have been assigned
    if (roomChanges.some(change => !change.newRoomId)) {
      setError('Please assign all rooms before completing the hotel change');
      return;
    }

    const requestBody: HotelChangeRequest = {
      newHotelId: selectedHotel,
      roomChanges
    };

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderDocumentId}/hotelChange`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update hotel/rooms');
      }

      const result = await response.json();
      console.log('Hotel/Room change successful:', result);
      setSnackbarInput({ message: 'Hotel/Room change successful', severity: 'success' });
      onClose();
    } catch (error) {
      console.error('Error changing hotel:', error);
      setError(error instanceof Error ? error.message : 'Failed to change hotel/rooms');
      setSnackbarInput({ message: error instanceof Error ? error.message : 'Failed to change hotel/rooms', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        <Typography variant="h6">Swap Hotel/Rooms - Order #{order?.confirmation}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {order && 
          <Grid container spacing={3}>
            {/* Order Information */}
            <Grid size={{ xs: 12}}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Booker:</strong> {order.booker?.firstName} {order.booker?.lastName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Current Hotel:</strong> {order.conference_hotel?.hotel?.longName || 'No hotel selected'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {roomBreakdown || 'No rooms in order'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Hotel Change Options - Only show on first step */}
            {activeStep === 0 && 
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Hotel Change Options
                  </Typography>
                  
                  {/* Switch for keep/swap hotel */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                      Keep Current Hotel
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={swapHotels}
                          onChange={handleSwapToggle}
                          color="primary"
                        />
                      }
                      label=""
                      sx={{ mx: 1 }}
                    />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Swap Hotels
                    </Typography>
                  </Box>

                  {!swapHotels && (
                    <Typography variant="body2" color="text.secondary">
                      The current hotel will be kept for this order. You can swap rooms in this hotel by clicking next.
                    </Typography>
                  )}

                  {/* Show hotel options only when swap is selected */}
                  {swapHotels && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Select a new hotel from the available options. You will then be able to select rooms for the new hotel on the next step.
                      </Typography>
                      {availableRooms.length > 0 ? (
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', mt: 2 }}>
                          {availableRooms.map((hotel) => {
                            const isSelected = selectedHotel === hotel.documentId;
                            const criteria = getHotelCriteriaStatus(hotel, requestedRooms);
                            
                            return (
                              <Card 
                                key={hotel.documentId} 
                                variant="outlined" 
                                sx={{ 
                                  border: isSelected ? '2px solid' : undefined,
                                  borderColor: isSelected ? 'primary.main' : undefined
                                }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    {criteria.matchesAll ? (
                                      <>
                                        <CheckCircleOutline sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                                          Matches all criteria
                                        </Typography>
                                      </>
                                    ) : (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {!criteria.maxOccupantsMatch && (
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <HighlightOff sx={{ color: 'error.main', mr: 0.5, fontSize: 16 }} />
                                            <Typography variant="caption" color="error.main">
                                              Max occupants
                                            </Typography>
                                          </Box>
                                        )}
                                        {!criteria.bedCountMatch && (
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <HighlightOff sx={{ color: 'error.main', mr: 0.5, fontSize: 16 }} />
                                            <Typography variant="caption" color="error.main">
                                              Bed count
                                            </Typography>
                                          </Box>
                                        )}
                                        {!criteria.dateAvailabilityMatch && (
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <HighlightOff sx={{ color: 'error.main', mr: 0.5, fontSize: 16 }} />
                                            <Typography variant="caption" color="error.main">
                                              Date availability
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {hotel.longName}
                                  </Typography>
                                  {hotel.rooms && Object.values(hotel.rooms).length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      <Box sx={{ mt: 1 }}>
                                        <Table>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Type</TableCell>
                                              <TableCell>Max 👥</TableCell>
                                              <TableCell>Rate</TableCell>
                                              <TableCell>Min Avail</TableCell>
                                              <TableCell>Max Avail</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                        {Object.values(hotel.rooms).map((room: RoomCal) => (
                                          <TableRow key={room.documentId}>
                                            <TableCell>{room.name}</TableCell>
                                            <TableCell>👥 {room.maxOccupants}</TableCell>
                                            <TableCell>${room.dailyRate?.toFixed(2) || 'N/A'}</TableCell>
                                            <TableCell>{getMinAvailableRoomsAcrossDates(room.availability)}</TableCell>
                                            <TableCell>{getMaxAvailableRoomsAcrossDates(room.availability)}</TableCell>
                                          </TableRow>
                                        ))}
                                        </TableBody>
                                        </Table>
                                      </Box>
                                    </Box>
                                  )}
                                  <Button
                                    variant={isSelected ? "contained" : "outlined"}
                                    fullWidth
                                    onClick={() => handleHotelSelect(hotel.documentId)}
                                  >
                                    {isSelected ? 'Selected' : 'Select'}
                                  </Button>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No available hotels found for this conference
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>}

            
            {activeStep === 1 && 
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Room Assignment
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Assign new rooms for each booking. Select the appropriate room from the new hotel for each existing booking in the order.
                    </Typography>
                    {order.order_rooms && order.order_rooms.length > 0 && selectedHotelData ? (
                      <Box sx={{ mt: 2 }}>
                        {order.order_rooms.map((room, index) => (
                          <Card key={room.documentId || index} sx={{ mb: 2 }} variant="outlined">
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 5 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      Current
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                      {order.conference_hotel?.hotel?.longName}
                                    </Typography>
                                    <Typography variant="body2">
                                      {room.conference_hotel_room?.name || 'Unknown Room'}
                                    </Typography>
                                    <Chip 
                                      label={room.type || 'Student'} 
                                      size="small" 
                                      sx={{ textTransform: 'capitalize', mt: 0.5 }}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {room.arrivalDate && room.departureDate && 
                                        `${formatDateFromYYYYMMDD(room.arrivalDate)} - ${formatDateFromYYYYMMDD(room.departureDate)}`
                                      }
                                    </Typography>
                                    {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                                      <Box sx={{ mt: 1 }}>
                                        {room.order_room_occupants.map((occupant, idx) => (
                                          <Chip
                                            key={occupant.documentId || idx}
                                            label={`${occupant.firstName} ${occupant.lastName}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ mr: 0.5, mb: 0.5 }}
                                          />
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Typography variant="body2" align="center">
                                      →
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      New
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      {selectedHotelData.longName}
                                    </Typography>
                                    <FormControl fullWidth>
                                      <InputLabel>Select New Room</InputLabel>
                                      <Select
                                        value={room.documentId ? (roomAssignments[room.documentId] || '') : ''}
                                        label="Select New Room"
                                        onChange={(e) => room.documentId && handleRoomAssignment(room.documentId, e.target.value)}
                                      >
                                        {selectedHotelData.rooms && Object.values(selectedHotelData.rooms).map((availableRoom: any) => {
                                          const minAvail = Math.min(...Object.values(availableRoom.availability || {}).map((avail: any) => avail.available || 0));
                                          const maxAvail = Math.max(...Object.values(availableRoom.availability || {}).map((avail: any) => avail.available || 0));
                                          // Check if this room meets all requirements for the room it is replacing
                                          const occupantCount = room.order_room_occupants?.length || 1;
                                          const meetsOccupants = (availableRoom.maxOccupants || 0) >= occupantCount;
                                          const meetsBeds = (availableRoom.bedCount || 0) >= (room.conference_hotel_room?.bedCount || 1);
                                          // Check availability for all dates in stay
                                          const stayDates = getDatesInStay(room.arrivalDate, room.departureDate);
                                          const meetsAvailability = stayDates.every(date => (availableRoom.availability?.[date]?.available || 0) > 0);
                                          const meetsAll = meetsOccupants && meetsBeds && meetsAvailability;
                                          return (
                                            <MenuItem key={availableRoom.documentId} value={availableRoom.documentId} style={meetsAll ? { color: 'green', fontWeight: 600 } : {}}>
                                              {availableRoom.name} - ${availableRoom.dailyRate?.toFixed(2) || 'N/A'}
                                              {' '}({occupantCount} occupants)
                                              {' '}Min: {minAvail}, Max: {maxAvail} available
                                              {meetsAll && ' ✔'}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                          <Button
                            onClick={handleBack}
                            sx={{ mr: 1 }}
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleChangeHotel}
                            disabled={loading || order.order_rooms?.some(room => room.documentId === undefined || !roomAssignments[room.documentId])}
                          >
                            {loading ? 'Changing Hotel...' : 'Complete Hotel Change'}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No rooms to assign or invalid hotel selection
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            }
          </Grid>
        }
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleProceedToRoomAssignment}
            disabled={swapHotels && !selectedHotel}
          >
            Next
          </Button>
        )}
      </DialogActions>
      
    </Dialog>
  );
}